import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useDirectorySignals } from '@/modules/directory-feed/components/directory-feed-provider'
import { invalidationsFor } from '@/modules/file-manager/lib/directory-events'
import { recentUploads } from '@/modules/file-manager/lib/recent-uploads'
import { makeQueryKeys } from '@/query/query-keys'

/**
 * How long events are collected before they become requests.
 *
 * Twenty files dropped into a shared folder publish twenty events within a
 * second or two, and twenty re-reads of one listing would be nineteen wasted.
 * A quarter of a second is short enough that nobody perceives it as lag and
 * long enough to catch a burst.
 */
const BATCH_MS = 250

/**
 * Turns what the channel says into re-reads, and is the only consumer that
 * does.
 *
 * **Batched, because the risk of invalidating is the storm.** Keys are
 * collected and de-duplicated by their serialized form, and one flush issues
 * each distinct key once however many events named it.
 *
 * **A failed recovery re-reads everything on screen, and that is the one time
 * a wide invalidation is right** — that, and a `directory.resync` from the
 * data plane, which says the same thing. Centrifugo says it could not replay the gap,
 * so nothing here knows what was missed, and the events that would have
 * corrected the view are gone. Anything pending is dropped with it: a handful
 * of narrow keys is no longer the answer to a question that just became "all
 * of it".
 *
 * **Nothing is suppressed because the actor is the person watching**, and the
 * issue asked for the opposite, so the reason belongs here. `actorId` names a
 * person, not a tab. Somebody with the file manager open twice who renames in
 * one window is the actor of the event the other window receives, and dropping
 * it would leave that second window stale — the exact failure the feed exists
 * to prevent. The echo in the acting tab costs one re-read of a listing that
 * was going to be re-read by its own mutation anyway. It would be worth
 * revisiting only alongside an optimistic write, and there is none in this
 * module today.
 */
export function useDirectoryInvalidation() {
	const queryClient = useQueryClient()
	const pending = useRef(new Map<string, unknown[]>())
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Cleared on unmount so a flush cannot fire into a torn-down tree. The
	// listener is removed by `useDirectorySignals`; the timer is ours.
	useEffect(
		() => () => {
			if (timer.current) {
				clearTimeout(timer.current)
			}
		},
		[]
	)

	const flush = () => {
		timer.current = null
		const keys = [...pending.current.values()]
		pending.current.clear()

		for (const key of keys) {
			// The promise is not awaited and not returned: this runs from a
			// timer with nobody to hand it to, and a failed refetch is
			// TanStack's to report through the query's own error state rather
			// than something to raise here.
			queryClient.invalidateQueries({ queryKey: key }).catch(() => {
				// Reported by the query that failed, not by the invalidation.
			})
		}
	}

	const schedule = (keys: unknown[][]) => {
		for (const key of keys) {
			// Serialized as the map's key, which is what makes two events
			// naming the same folder one entry rather than two.
			pending.current.set(JSON.stringify(key), key)
		}

		timer.current ??= setTimeout(flush, BATCH_MS)
	}

	// Everything pending is dropped and both roots are re-read at once.
	const resyncAll = () => {
		pending.current.clear()
		if (timer.current) {
			clearTimeout(timer.current)
			timer.current = null
		}
		// Both roots, because «everything you hold is suspect» includes the
		// search results: they are rows about names and places, the gap the
		// channel could not replay is full of changes to exactly those, and
		// nothing under `directory` reaches the `search` prefix (OPSSTD-8135).
		// And the storage meter: a `storage.changed` may be in the gap too.
		for (const root of [
			makeQueryKeys.directory.all(),
			makeQueryKeys.search.all(),
			makeQueryKeys.users.meStorage()
		]) {
			queryClient.invalidateQueries({ queryKey: root }).catch(() => {
				// As above: the query that failed is what reports it.
			})
		}
	}

	useDirectorySignals((signal) => {
		// `directory.resync` is the same news as a failed recovery, told by the
		// data plane: a burst too wide to send event by event, replaced by this
		// one. Batching it with narrow keys would only delay the answer that
		// already covers them.
		if (
			signal.kind === 'desync' ||
			signal.event.type === 'directory.resync'
		) {
			resyncAll()
			return
		}

		// Not an invalidation, and recorded here because this is the one place
		// every event of the session passes: the grid holds off making a
		// thumbnail for a file finished a moment ago (`recent-uploads.ts`).
		if (signal.event.type === 'node.uploaded' && signal.event.nodeId) {
			recentUploads.note(signal.event.nodeId)
		}

		schedule(invalidationsFor(signal.event) as unknown[][])
	})
}
